// * What does it do:
// - Turns the different icon inputs of the buttons into a rendered icon: a registry key, a CMS icon relation, an icon
//   component or a ready element (optionally resized)

// * Who calls it:
// - Button (and ButtonOld), ButtonCountdown, ButtonMenu, ModalTempSurvey

// * Which data is needed:
// - The icon input and an optional size in px

// * What comes back:
// - The icon node, or null if nothing matches

import {
  cloneElement,
  ComponentType,
  createElement,
  isValidElement,
  ReactElement,
  ReactNode,
} from 'react'
import { getIconByKey } from '@/components/icons/IconRegistry'
import { ButtonIcon } from '@/lib/directus-schema'

type IconSizeProps = { width?: number; height?: number }

export type IconInput = string | ReactElement | ComponentType<IconSizeProps> | ButtonIcon

export function resolveIcon(icon?: IconInput, size?: number): ReactNode {
  if (!icon) return null
  const normalizedSize =
    typeof size === 'number' && Number.isFinite(size) && size > 0 ? Math.floor(size) : null
  const iconSizeProps = normalizedSize
    ? { width: normalizedSize, height: normalizedSize }
    : undefined

  if (typeof icon === 'string') {
    const IconByKey = getIconByKey(icon)
    return IconByKey ? <IconByKey {...iconSizeProps} /> : null
  }

  // Before the CMS icon check: React elements have a `key` property too.
  if (isValidElement(icon)) {
    if (!iconSizeProps) return icon
    return cloneElement(icon as ReactElement<IconSizeProps>, iconSizeProps)
  }

  if (typeof icon === 'object' && 'key' in icon) {
    const key = (icon as ButtonIcon).key
    if (typeof key === 'string' && key.length > 0) {
      const IconByKey = getIconByKey(key)
      return IconByKey ? <IconByKey {...iconSizeProps} /> : null
    }
  }

  if (typeof icon === 'function') {
    return createElement(icon, iconSizeProps)
  }

  return null
}
